'use client';

import { useEffect, useState } from 'react';
import { Check, Download, Play } from 'lucide-react';

// Step 5: Export
export default function ExportMockup() {
  const [phase, setPhase] = useState<'ready' | 'exporting' | 'done'>('ready');
  const [progress, setProgress] = useState(0);

  useEffect(() => {
    const runCycle = () => {
      setPhase('ready');
      setProgress(0);

      setTimeout(() => {
        setPhase('exporting');
        let p = 0;
        const interval = setInterval(() => {
          p += 2;
          setProgress(Math.min(p, 100));
          if (p >= 100) {
            clearInterval(interval);
            setPhase('done');
            setTimeout(runCycle, 2500);
          }
        }, 50);
      }, 1500);
    };
    runCycle();
  }, []);

  return (
    <div className="relative w-full rounded-2xl overflow-hidden bg-gradient-to-br from-green-200 via-amber-100 to-violet-200 dark:from-green-800/40 dark:via-amber-700/30 dark:to-violet-800/40 p-2 sm:p-3 lg:p-3.5">
      <div className="w-full h-[320px] bg-background p-4 sm:p-5 rounded-xl">
        <div className="h-full flex flex-col gap-2">
          {/* Preview thumbnail with simulated content */}
          <div className="h-28 sm:h-32 bg-gradient-to-br from-slate-900 to-slate-800 rounded-xl relative overflow-hidden">
            {/* Simulated video content */}
            <div className="absolute inset-3 bg-slate-800/80 rounded-lg overflow-hidden">
              {/* Mini dashboard preview */}
              <div className="h-full flex">
                {/* Sidebar */}
                <div className="w-7 bg-slate-700/50 p-1 space-y-1">
                  <div className="w-full h-2 bg-videomule-green/40 rounded" />
                  <div className="w-full h-2 bg-slate-600 rounded" />
                  <div className="w-full h-2 bg-slate-600 rounded" />
                </div>
                {/* Main content */}
                <div className="flex-1 p-1.5 space-y-1">
                  <div className="flex gap-1">
                    <div className="flex-1 h-3 bg-videomule-green/30 rounded" />
                    <div className="flex-1 h-3 bg-blue-500/30 rounded" />
                    <div className="flex-1 h-3 bg-purple-500/30 rounded" />
                  </div>
                  <div className="h-10 bg-slate-700/50 rounded flex items-end p-1 gap-0.5">
                    {[35, 55, 40, 75, 50, 65, 85, 60].map((h, i) => (
                      <div
                        key={i}
                        className="flex-1 bg-videomule-green/60 rounded-t"
                        style={{ height: `${h}%` }}
                      />
                    ))}
                  </div>
                  <div className="flex gap-1">
                    <div className="h-2 bg-slate-600 rounded flex-1" />
                    <div className="h-2 bg-slate-600/60 rounded w-1/3" />
                  </div>
                </div>
              </div>
            </div>

            {/* Timestamp badge */}
            <div className="absolute top-2 left-2 bg-videomule-green/90 px-2 py-1 rounded-md text-xs text-white font-mono font-medium">
              02:34
            </div>

            {/* Play button overlay (when ready) */}
            {phase === 'ready' && (
              <div className="absolute inset-0 flex items-center justify-center">
                <div className="w-10 h-10 rounded-full bg-white/20 backdrop-blur-sm flex items-center justify-center">
                  <Play className="w-5 h-5 text-white ml-0.5" />
                </div>
              </div>
            )}

            {/* Done overlay */}
            {phase === 'done' && (
              <div className="absolute inset-0 bg-videomule-green/20 backdrop-blur-[2px] rounded-xl flex items-center justify-center animate-fade-in">
                <div className="w-14 h-14 rounded-full bg-videomule-green flex items-center justify-center shadow-xl shadow-videomule-green/40 animate-scale-in">
                  <Check className="w-7 h-7 text-white" />
                </div>
              </div>
            )}
          </div>

          {/* Export content */}
          <div className="flex-1 flex flex-col justify-center min-h-0">
            {phase === 'ready' && (
              <div className="text-center animate-fade-in">
                <p className="text-foreground font-medium mb-3">
                  Ready to export
                </p>
                <div className="inline-flex items-center gap-2 px-5 py-2.5 bg-videomule-green text-white rounded-lg text-sm font-medium shadow-lg shadow-videomule-green/25 animate-pulse">
                  <Download className="w-4 h-4" />
                  Export Now
                </div>
              </div>
            )}

            {phase === 'exporting' && (
              <div className="space-y-3 animate-fade-in">
                <div className="flex items-center justify-between text-sm">
                  <span className="text-foreground font-medium">
                    Exporting...
                  </span>
                  <span className="text-videomule-green font-bold">
                    {progress}%
                  </span>
                </div>
                <div className="h-2.5 bg-muted rounded-full overflow-hidden">
                  <div
                    className="h-full bg-gradient-to-r from-videomule-green to-videomule-green-hover rounded-full transition-all"
                    style={{ width: `${progress}%` }}
                  />
                </div>
                <p className="text-xs text-muted-foreground text-center">
                  Rendering video • Optimizing audio
                </p>
              </div>
            )}

            {phase === 'done' && (
              <div className="text-center animate-fade-in space-y-1">
                <p className="text-videomule-green font-semibold text-base sm:text-lg">
                  Export Complete!
                </p>
                <p className="text-muted-foreground text-xs sm:text-sm">
                  tutorial_final.mp4 • 24.5 MB
                </p>
                <button className="inline-flex items-center gap-2 px-3 sm:px-4 py-1.5 sm:py-2 bg-videomule-green hover:bg-videomule-green-hover text-white rounded-lg text-xs sm:text-sm font-medium transition-colors shadow-lg shadow-videomule-green/25 mt-2">
                  <Download className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
                  Download Video
                </button>
              </div>
            )}
          </div>

          {/* Format pills */}
          <div className="flex items-center justify-between pt-2 sm:pt-3 border-t border-border mt-auto">
            <div className="flex gap-1">
              {['MP4', 'MOV', 'WebM'].map((f, i) => (
                <span
                  key={f}
                  className={`px-2 sm:px-3 py-1 sm:py-1.5 rounded-md text-[10px] sm:text-xs font-medium ${
                    i === 0
                      ? 'bg-videomule-green text-white'
                      : 'bg-muted text-muted-foreground'
                  }`}
                >
                  {f}
                </span>
              ))}
            </div>
            <span className="text-[10px] sm:text-xs text-muted-foreground">
              4K UHD
            </span>
          </div>
        </div>
      </div>
    </div>
  );
}
